'use client';

import Button from '@enlighten/design-system/dist/Button';
import List from '@enlighten/design-system/dist/List';
import ListItem from '@enlighten/design-system/dist/ListItem';
import ListItemButton from '@enlighten/design-system/dist/ListItemButton';
import Typography from '@enlighten/design-system/dist/Typography';
import { useState } from 'react';

import ArrowIcon from '@/assets/images/arrow.svg';
import AndyAvatar from '@/assets/images/members/andy.png';
import AngelAvatar from '@/assets/images/members/angel.png';
import BenAvatar from '@/assets/images/members/ben.png';
import ENlightenLogo from '@/assets/images/members/logo.png';
import NamsanAvatar from '@/assets/images/members/namsan.png';
import ExchangeBackground from '@/assets/images/pages/home/drawer/exchange.jpg';
import FinanceBackground from '@/assets/images/pages/home/drawer/finance.jpg';
import InsightBackground from '@/assets/images/pages/home/drawer/insight.jpg';
import InsuranceBackground from '@/assets/images/pages/home/drawer/insurance.jpg';
import KingBackground from '@/assets/images/pages/home/drawer/king.jpg';
import MoplusBackground from '@/assets/images/pages/home/drawer/moplus.jpg';
import OnmBackground from '@/assets/images/pages/home/drawer/onm.jpg';
import SellBackground from '@/assets/images/pages/home/drawer/sell.jpg';
import SolarBridgeBackground from '@/assets/images/pages/home/drawer/solarbridge.jpg';

import type { ITutorialStepComponent } from '..';
import TutorialGridTemplate from '../TutorialGridTemplate';
import type { TutorialCardProps } from '../TutorialGridTemplate/TutorialCard';
import TutorialLayout from '../TutorialLayout';

const sortPivot = ['insight', 'exchange', 'solarbridge', 'king', 'onm', 'rtu', 'insurance', 'finance', 'sell'];

const list: ({ id: typeof sortPivot[number] } & TutorialCardProps)[] = [
	{
		id: 'insight',
		background: InsightBackground,
		avatar: ENlightenLogo,
		title: '인사이트',
		subTitle: '하루도 놓치면 안 될 에너지 소식',
		comment: '"에너지 비즈니스에 도움을 드리는 유용한 지식을 전달합니다"',
		linkProps: {
			href: '/insight',
		},
	},
	{
		id: 'exchange',
		background: ExchangeBackground,
		avatar: BenAvatar,
		title: '전력중개',
		subTitle: '시장 최고 수익 보장',
		comment: '"전력중개사업 참여하고 발전 기간 내내 추가 수익 챙기세요"',
		linkProps: {
			href: '/exchange',
		},
	},
	{
		id: 'solarbridge',
		background: SolarBridgeBackground,
		avatar: ENlightenLogo,
		title: '솔라브리지',
		subTitle: '평균 수익률 11% 발전소 투자',
		comment: '"안정적인 사업만을 엄선하여 투자자들에게 소개합니다"',
		linkProps: {
			href: 'https://www.solarbridge.kr',
			target: '_blank',
		},
	},
	{
		id: 'king',
		background: KingBackground,
		avatar: BenAvatar,
		title: '발전왕',
		subTitle: '스마트폰으로 간편하게 발전소 관리',
		comment: '"발전량부터 수익까지 스마트폰으로 쉽게 모니터링 해보세요"',
		linkProps: {
			href: '/king',
		},
	},
	{
		id: 'onm',
		background: OnmBackground,
		avatar: AngelAvatar,
		title: '발전소 O&M',
		subTitle: '전문가가 진행하는 발전소 관리',
		comment: '"발전소 점검부터 회계 대행 및 사무수탁까지 편리하게 이용하세요"',
		linkProps: {
			href: '/',
		},
	},
	{
		id: 'rtu',
		background: MoplusBackground,
		avatar: BenAvatar,
		title: '발전왕 모니터링(RTU)',
		subTitle: '월 2만원으로 모니터링 업그레이드',
		comment: '"저렴하고 확실한 모니터링 서비스로 데이터를 실시간으로 확인하세요"',
		linkProps: {
			href: 'https://enlighten.kr/rtu',
			target: '_blank',
		},
	},
	{
		id: 'insurance',
		background: InsuranceBackground,
		avatar: AndyAvatar,
		title: '발전소 보험',
		subTitle: '자연재해까지 보장하는 정책성 보험',
		comment: '"엔라이튼을 통해 개별 가입 대비 20% 저렴하게 가입하세요"',
		linkProps: {
			href: 'https://enlighten.kr/insurance/hanwha',
			target: '_blank',
		},
	},
	{
		id: 'finance',
		background: FinanceBackground,
		avatar: AndyAvatar,
		title: '발전소 대환대출',
		subTitle: 'PF 대출 더 낮은 금리로 갈아타기',
		comment: '"900kW 이상 발전소라면 금리를 따져보고 결정하세요"',
		linkProps: {
			href: '/finance',
		},
	},
	{
		id: 'sell',
		background: SellBackground,
		avatar: NamsanAvatar,
		title: '발전소 매각',
		subTitle: '2주 이내 초고속 자금 집행',
		comment: '"태양광 전문 법인이 사업 가치를 평가하고 직접 매입합니다"',
		linkProps: {
			href: '/site-sale',
		},
	},
];

const selections = [
	{
		id: 1,
		title: '발전소 매출 하락',
		value: ['exchange', 'solarbridge'],
	},
	{
		id: 2,
		title: '낮은 발전량',
		value: ['king', 'onm'],
	},
	{
		id: 3,
		title: '불편한 발전소 모니터링',
		value: ['king', 'rtu'],
	},
	{
		id: 4,
		title: '비싼 모니터링 서비스 요금',
		value: ['rtu'],
	},
	{
		id: 5,
		title: '노후화된 설비',
		value: ['onm', 'insurance'],
	},
	{
		id: 6,
		title: '대출금 상환 부담',
		value: ['finance', 'sell'],
	},
	{
		id: 7,
		title: '시장 정보 부족',
		value: ['insight'],
	},
	{
		id: 8,
		title: '자연재해, 사고 우려',
		value: ['insurance'],
	},
];

export default function Owner({ onChangeComponent }: ITutorialStepComponent) {
	const [idList, setIdList] = useState<number[]>([]);
	const [submitted, setSubmitted] = useState<boolean>(false);

	return submitted ? (
		<TutorialGridTemplate
			title={'전문가 맞춤 서비스로\n운영 효율을 높여보세요'}
			list={[...new Set(idList.map((id) => selections.find((item) => item.id === id)?.value || []).flat())]
				.sort((a, b) => (sortPivot.indexOf(a) < sortPivot.indexOf(b) ? -1 : 1))
				.map((id) => list.find((item) => item.id === id) as TutorialCardProps)}
			onChangeComponent={onChangeComponent}
		/>
	) : (
		<TutorialLayout
			visibleAvatar
			title={
				<>
					<Typography fontSize={{ xs: 26, md: 34 }} fontWeight={700} lineHeight={{ xs: 1.3, md: '44px' }}>
						{'사업 운영 중\n어떤 어려움이 있으신가요?'}
					</Typography>
					<Typography fontSize={{ xs: 14, md: 16 }} color="grey.800" marginRight={1}>
						해당하는 항목을 모두 선택해 주세요
					</Typography>
				</>
			}
			onChangeComponent={onChangeComponent}
		>
			<List
				disablePadding
				sx={{
					display: { xs: 'flex', md: 'grid' },
					flexDirection: 'column',
					gridTemplateColumns: '1fr 1fr',
					gap: 2,
					marginTop: 2,
				}}
			>
				{selections.map(({ id, title }, i) => (
					<ListItem key={i} disablePadding disableGutters>
						<ListItemButton
							className={idList.includes(id) ? 'active' : undefined}
							onClick={() => {
								setIdList((state) => (state.includes(id) ? state.filter((_id) => _id !== id) : [...state, id]));
							}}
							sx={(theme) => ({
								border: `1px solid ${theme.palette.grey['400']}`,
								borderRadius: '4px',
								height: 56,
								padding: '13.5px 24px',
								cursor: 'pointer',
								transition: 'all 0.2s ease',
								':hover, &.active': {
									bgcolor: '#F9F6FF',
									borderColor: 'primary.light',
									'.MuiTypography-root': {
										color: 'primary.light',
										fontWeight: 700,
									},
								},
								'.MuiTypography-root': {
									transition: 'all 0.2s ease',
								},
							})}
						>
							<Typography fontSize={16}>{title}</Typography>
						</ListItemButton>
					</ListItem>
				))}
			</List>
			<Button
				disabled={idList.length === 0}
				onClick={() => {
					if (idList.length > 0) {
						setSubmitted(true);
					}
				}}
				sx={{ height: 60, borderRadius: '6px' }}
			>
				<Typography fontSize={18} fontWeight={700} color="common.white" marginRight={1}>
					해결방법 찾기
				</Typography>
				<ArrowIcon fill="white" />
			</Button>
		</TutorialLayout>
	);
}
